<script>
  // Полноэкранный редактор правки своего сообщения (1.24.1).
  //
  // Раньше правка шла в композере с потолком 150px: три строки текста, лупа
  // курсора на iPhone в таком окошке бесполезна. Здесь поле занимает всю высоту
  // приложения минус клавиатура (--app-height из viewport.js), прокрутка и
  // навигация по тексту — нативные. Композер и его жесты не трогаются.
  import { createEventDispatcher, tick } from 'svelte';
  import { TEXT } from '../ui-text.js';

  export let open = false;
  export let text = '';
  export let images = [];
  // Сколько сообщений уйдёт ниже (обмен правленого и всё после него).
  export let tailCount = 0;
  export let busy = false;
  export let error = '';

  const dispatch = createEventDispatcher();

  let panelElement = null;
  let fieldElement = null;
  let previouslyFocused = null;
  let draftText = '';
  let draftImages = [];
  let keepTail = false;

  $: if (open) {
    startEditing();
  } else {
    restoreFocus();
  }

  $: canSubmit = !busy && Boolean(draftText.trim() || draftImages.length);

  async function startEditing() {
    previouslyFocused = typeof document !== 'undefined' ? document.activeElement : null;
    draftText = text || '';
    draftImages = Array.isArray(images) ? [...images] : [];
    keepTail = false;
    await tick();
    fieldElement?.focus();
  }

  function restoreFocus() {
    // На поле ввода фокус не возвращаем: iOS поднял бы клавиатуру над лентой.
    if (
      previouslyFocused
      && typeof previouslyFocused.focus === 'function'
      && !previouslyFocused.matches?.('textarea, input')
    ) {
      previouslyFocused.focus();
    }
    previouslyFocused = null;
  }

  function cancel() {
    dispatch('cancel');
  }

  function submit() {
    if (!canSubmit) {
      return;
    }
    dispatch('submit', { text: draftText, images: draftImages, keepTail });
  }

  function removeImageAt(index) {
    draftImages = draftImages.filter((_, i) => i !== index);
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      cancel();
      return;
    }
    // Enter — новая строка (правят длинные тексты), отправка — Ctrl/Cmd+Enter.
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    }
  }
</script>

{#if open}
  <div
    class="editor"
    bind:this={panelElement}
    role="dialog"
    aria-modal="true"
    aria-label={TEXT.edit.banner}
    tabindex="-1"
    data-no-swipe
    data-testid="message-editor"
    on:keydown={handleKeydown}
  >
    <header class="editor-head">
      <span class="editor-title">{TEXT.edit.banner}</span>
      <button type="button" class="editor-cancel" on:click={cancel} disabled={busy}>{TEXT.edit.cancel}</button>
    </header>

    <div class="editor-tail">
      {#if tailCount > 0}
        <label class="editor-toggle">
          <input type="checkbox" bind:checked={keepTail} disabled={busy} />
          <span>{TEXT.edit.tailKeep}</span>
        </label>
        <p class="editor-hint">
          {keepTail ? TEXT.edit.keepHint : TEXT.edit.tailDelete.replace('{count}', String(tailCount))}
        </p>
      {:else}
        <p class="editor-hint">{TEXT.edit.tailNone}</p>
      {/if}
    </div>

    {#if draftImages.length}
      <div class="editor-images">
        {#each draftImages as image, i (i)}
          <div class="editor-image">
            <img src={image.dataUrl} alt={`Вложение ${i + 1}`} />
            <button
              type="button"
              class="editor-image-remove"
              aria-label={TEXT.edit.removeImage}
              title={TEXT.edit.removeImage}
              disabled={busy}
              on:click={() => removeImageAt(i)}
            >×</button>
          </div>
        {/each}
      </div>
    {/if}

    <textarea
      class="editor-field"
      bind:this={fieldElement}
      bind:value={draftText}
      aria-label={TEXT.edit.fieldLabel}
      placeholder={TEXT.edit.fieldLabel}
      autocapitalize="sentences"
      disabled={busy}
    ></textarea>

    {#if error}
      <p class="editor-error" role="alert">{error}</p>
    {/if}

    <footer class="editor-foot">
      <button type="button" class="editor-submit" on:click={submit} disabled={!canSubmit}>
        {busy ? TEXT.edit.sending : TEXT.edit.send}
      </button>
    </footer>
  </div>
{/if}

<style>
  .editor {
    position: fixed;
    inset: 0;
    /* Выше ленты и плавающих кнопок, ниже флеша (App.svelte, 200); шторки (300)
       к этому моменту закрыты. Высота — из viewport.js: клавиатура iOS её сжимает. */
    z-index: 190;
    height: var(--app-height, 100dvh);
    display: flex;
    flex-direction: column;
    background: var(--bg-primary);
    color: var(--text-primary);
    padding-top: env(safe-area-inset-top, 0px);
    outline: none;
  }

  .editor-head,
  .editor-tail,
  .editor-images,
  .editor-foot {
    flex: none;
    padding-left: calc(var(--spacing-lg) + env(safe-area-inset-left, 0px));
    padding-right: calc(var(--spacing-lg) + env(safe-area-inset-right, 0px));
  }

  .editor-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
  }

  .editor-title {
    font-size: 0.95rem;
    font-weight: 600;
  }

  .editor-cancel {
    min-height: 44px;
    padding: 0.4rem 0.9rem;
    border: 1px solid var(--border-color);
    border-radius: 12px;
    background: transparent;
    color: var(--text-secondary);
    font: inherit;
    font-size: 0.9rem;
    cursor: pointer;
  }

  .editor-tail {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--border-color);
  }

  .editor-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-height: 32px;
    font-size: 0.9rem;
  }

  .editor-hint {
    margin: 0;
    font-size: 0.8rem;
    color: var(--text-secondary);
  }

  .editor-images {
    display: flex;
    gap: 0.5rem;
    overflow-x: auto;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }

  .editor-image {
    position: relative;
    flex: none;
  }

  .editor-image img {
    display: block;
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 8px;
  }

  .editor-image-remove {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: var(--error-text);
    color: var(--bg-primary);
    font-size: 16px;
    line-height: 24px;
    cursor: pointer;
  }

  .editor-field {
    flex: 1;
    min-height: 0;
    width: 100%;
    margin: 0;
    padding: var(--spacing-lg);
    padding-left: calc(var(--spacing-lg) + env(safe-area-inset-left, 0px));
    padding-right: calc(var(--spacing-lg) + env(safe-area-inset-right, 0px));
    border: none;
    border-radius: 0;
    background: var(--bg-primary);
    color: var(--text-primary);
    font: inherit;
    font-size: max(16px, 1rem);
    line-height: 1.5;
    resize: none;
    overflow-y: auto;
    overscroll-behavior: contain;
    touch-action: pan-y;
    -webkit-user-select: text;
    user-select: text;
  }

  .editor-field:focus {
    outline: none;
  }

  .editor-error {
    flex: none;
    margin: 0;
    padding: 0.4rem var(--spacing-lg);
    font-size: 0.85rem;
    color: var(--error-text);
  }

  .editor-foot {
    padding-top: 0.6rem;
    padding-bottom: calc(0.6rem + env(safe-area-inset-bottom, 0px));
    background: var(--bg-secondary);
    border-top: 1px solid var(--border-color);
  }

  .editor-submit {
    width: 100%;
    min-height: 48px;
    border: 1px solid var(--accent-color);
    border-radius: 12px;
    background: var(--accent-color);
    color: var(--bg-primary);
    font: inherit;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
  }

  .editor-submit:disabled,
  .editor-cancel:disabled {
    opacity: 0.5;
    cursor: default;
  }
</style>
